/**
 * Entry TypeScript Types
 *
 * Types for the Entry entity - captured content from which
 * domain entities (tasks, moments) are derived.
 */

export type EntryType =
  | 'conversation'
  | 'meeting'
  | 'journal'
  | 'check_in'
  | 'voice_memo'
  | 'brainstorm'
  | 'call'
  | 'email'
  | 'message';

export type SourceSystem =
  | 'plaud'
  | 'otter'
  | 'whisper'
  | 'zoom'
  | 'teams'
  | 'google_meet'
  | 'manual'
  | 'email'
  | 'whatsapp';

export type ProcessingStatus =
  | 'pending'
  | 'processing'
  | 'complete'
  | 'failed'
  | 'partial';

export interface UserOverrides {
  title?: string | null;
  synopsis?: string | null;
  entryType?: string | null;
  tags?: string[] | null;
  description?: string | null;
  linkedCalendarEventId?: string | null;
}

export interface OverrideMetadata {
  hasUserOverrides: boolean;
  overriddenAt?: string | null;
  overriddenFields: string[];
}

export interface SourceMetadata {
  rawContentPath?: string | null;
  processedContentPath?: string | null;
  rawTranscript?: string | null;
  language?: string | null;
  speakerCount?: number | null;
  wordCount?: number | null;
  plaudTemplateId?: string | null;
  plaudTemplateName?: string | null;
  plaudAiContent?: string | null;
  otterSpeechId?: string | null;
  otterFolder?: string | null;
  otterRecordingMode?: string | null;
  calendarEventId?: string | null;
  meetingUrl?: string | null;
  attendees?: Array<{
    name: string;
    email?: string;
    role?: string;
  }> | null;
  extra?: Record<string, unknown> | null;
}

export interface Entry {
  id: string;
  tenantId: string;

  // Classification
  entryType: EntryType;
  title: string;
  synopsis?: string | null;

  // Timing
  recordedAt?: string | null;
  durationSeconds?: number | null;

  // External Source
  sourceSystem: SourceSystem;
  sourceId: string;
  sourceMetadata?: SourceMetadata | null;

  // Content References
  transcriptId?: string | null;

  // Processing State
  processingStatus: ProcessingStatus;
  extractionNotes?: string | null;

  // User Overrides
  userOverrides?: UserOverrides | null;
  overrideMetadata?: OverrideMetadata | null;

  // Timestamps
  createdAt?: string | null;
  updatedAt?: string | null;
  syncedAt?: string | null;

  // Effective values (computed)
  effectiveTitle: string;
  effectiveEntryType: string;
  effectiveSynopsis?: string | null;
  effectiveTags: string[];
}

export interface EntryAssociations {
  entryId: string;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    verificationStatus?: string;
    createdAt?: string;
  }>;
  taskCount: number;
}

export interface EntryFilters {
  entryType?: EntryType;
  sourceSystem?: SourceSystem;
  recordedAfter?: string;
  recordedBefore?: string;
  search?: string;
  hasOverrides?: boolean;
  processingStatus?: ProcessingStatus;
  sortBy?: 'recordedAt' | 'createdAt' | 'updatedAt' | 'title';
  sortOrder?: 'asc' | 'desc';
}

export interface EntryListResponse {
  entries: Entry[];
  total: number;
  count: number;
  limit: number;
  offset: number;
}

export interface EntryDetailResponse {
  entry: Entry;
  associations?: EntryAssociations;
}

export interface UpdateEntryInput {
  title?: string;
  synopsis?: string;
  entryType?: string;
  tags?: string[];
  description?: string;
}

export interface EntryStats {
  total: number;
  byType: Record<string, number>;
  bySource: Record<string, number>;
  byStatus: Record<string, number>;
  withOverrides: number;
}

export interface EntryTypeOption {
  value: EntryType;
  label: string;
  icon: string;
}

// Entry type icons
export const ENTRY_TYPE_ICONS: Record<EntryType, string> = {
  conversation: '💬',
  meeting: '📅',
  journal: '📝',
  check_in: '✅',
  voice_memo: '🎤',
  brainstorm: '💭',
  call: '📞',
  email: '📧',
  message: '💬',
};

// Entry type labels
export const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  conversation: 'Conversation',
  meeting: 'Meeting',
  journal: 'Journal',
  check_in: 'Check-in',
  voice_memo: 'Voice Memo',
  brainstorm: 'Brainstorm',
  call: 'Phone Call',
  email: 'Email',
  message: 'Message',
};

// Source system labels
export const SOURCE_SYSTEM_LABELS: Record<SourceSystem, string> = {
  plaud: 'Plaud',
  otter: 'Otter.ai',
  whisper: 'Whisper',
  zoom: 'Zoom',
  teams: 'Teams',
  google_meet: 'Google Meet',
  manual: 'Manual',
  email: 'Email',
  whatsapp: 'WhatsApp',
};

// Helper to format duration
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return '--';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}
